import React from 'react';
import { Tooltip } from '../Tooltip';
import { ui } from '../../config/uiSizes';

interface NotificationRecipientsProps {
    /** Current recipient email addresses. */
    recipients: string[];
    /** Called with the updated list whenever a recipient is added or removed. */
    onChange: (recipients: string[]) => void;
}

// Pragmatic email shape check — mirrors the backend's up-front validation intent
// (a clear inline error instead of a silent no-op at send time).
const isValidEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * Recipient editor for recurring-migration failure notifications. Sends the addresses to the
 * backend as notificationSettings.recipients. The migration creator is always notified too, so
 * this list is additive.
 */
const NotificationRecipients: React.FC<NotificationRecipientsProps> = ({
    recipients,
    onChange
}) => {
    const [inputValue, setInputValue] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);

    const addRecipient = () => {
        const candidate = inputValue.trim();
        if (candidate.length === 0) {
            return;
        }
        if (!isValidEmail(candidate)) {
            setError('Enter a valid email address.');
            return;
        }
        const alreadyAdded = recipients.some(
            (r) => r.toLowerCase() === candidate.toLowerCase()
        );
        if (alreadyAdded) {
            setError('That address has already been added.');
            return;
        }
        onChange([...recipients, candidate]);
        setInputValue('');
        setError(null);
    };

    const removeRecipient = (target: string) => {
        onChange(recipients.filter((r) => r !== target));
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Enter or comma commits the current address.
        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            addRecipient();
        }
    };

    return (
        <div
            className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg ${ui.page.sectionPadding}`}
        >
            <div className={`flex items-center ${ui.gap.sm} ${ui.mb.sm}`}>
                <svg
                    className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                </svg>
                <span
                    className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-200`}
                >
                    Notify on failure
                </span>
                <Tooltip
                    content="(Optional) Additional email addresses to notify if a scheduled run fails. The person who creates this migration is always notified."
                    position="right"
                    className={`${ui.text.formSectionLabel} font-semibold`}
                />
            </div>

            <div className={`flex items-center ${ui.gap.sm}`}>
                <input
                    type="email"
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        if (error) setError(null);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="name@example.com"
                    aria-label="Notification recipient email address"
                    className={`flex-1 ${ui.input.base} ${ui.text.body} rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <button
                    type="button"
                    onClick={addRecipient}
                    className={`${ui.button.sm} rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-semibold hover:bg-gray-300 dark:hover:bg-gray-600`}
                >
                    Add
                </button>
            </div>

            {error && (
                <p className={`${ui.text.xs} text-red-600 dark:text-red-400 ${ui.mt.sm}`}>
                    {error}
                </p>
            )}

            {recipients.length > 0 && (
                <div className={`flex flex-wrap ${ui.gap.sm} ${ui.mt.md}`}>
                    {recipients.map((recipient) => (
                        <span
                            key={recipient}
                            className={`inline-flex items-center ${ui.gap.xs} ${ui.badge.padding} ${ui.text.xs} rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100`}
                        >
                            {recipient}
                            <button
                                type="button"
                                onClick={() => removeRecipient(recipient)}
                                aria-label={`Remove ${recipient}`}
                                className="text-blue-600 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-50"
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default NotificationRecipients;
